import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AnouncementsService from '../../../Api/AnouncementsService';
import CourseService from '../../../Api/CourseService';

const ViewAnouncements = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const announcementId = new URLSearchParams(location.search).get('id');
    const announcementAPI = new AnouncementsService();
    const courseAPI = new CourseService();
    const [announcement, setAnnouncement] = useState(null);
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            if (!announcementId) {
                navigate('/announcements-list');
                return;
            }

            try {
                setLoading(true);
                const [announcementResponse, coursesResponse] = await Promise.all([
                    announcementAPI.GetById(announcementId),
                    courseAPI.GetAllCourseList()
                ]);

                if (announcementResponse.data && announcementResponse.data.data) {
                    setAnnouncement(announcementResponse.data.data);
                }

                if (coursesResponse.data && coursesResponse.data.data) {
                    setCourses(coursesResponse.data.data);
                }
            } catch (err) {
                setError('Failed to load announcement details. Please try again.');
                console.error('Error loading announcement:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [announcementId, navigate]);

    if (loading) {
        return (
            <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger" role="alert">
                {error}
            </div>
        );
    }

    if (!announcement) {
        return null;
    }

    // Get course titles for the selected course IDs
    const selectedCourses = courses.filter(course => 
        announcement.course_ids?.includes(course.id)
    );

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-24">
                    <h4 className="text-xl text-primary-light">View Announcement</h4>
                    <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => navigate('/announcements-list')}
                    >
                        Back to List
                    </button>
                </div>

                <div className="mb-4">
                    <h5 className="text-muted mb-2">Title</h5>
                    <p className="h4">{announcement.title}</p>
                </div>

                <div className="mb-4">
                    <h5 className="text-muted mb-2">Content</h5>
                    <p className="text-justify">{announcement.content}</p>
                </div>

                <div className="mb-4">
                    <h5 className="text-muted mb-2">Type</h5>
                    <span className={`badge ${announcement.type === 'public' ? 'bg-success' : 'bg-warning'}`}>
                        {announcement.type.charAt(0).toUpperCase() + announcement.type.slice(1)}
                    </span>
                </div>

                <div className="mb-4">
                    <h5 className="text-muted mb-2">Associated Courses</h5>
                    {selectedCourses.length > 0 ? (
                        <ul className="list-group">
                            {selectedCourses.map(course => (
                                <li key={course.id} className="list-group-item">
                                    {course.title}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-muted">No courses associated</p>
                    )}
                </div>

                <div className="mb-4">
                    <h5 className="text-muted mb-2">Created At</h5>
                    <p>{new Date(announcement.created_at).toLocaleDateString()}</p>
                </div>
            </div>
        </div>
    );
};

export default ViewAnouncements;
